import React, { useEffect, useRef } from 'react';
import { ADSENSE_CLIENT, SHOW_AD_PLACEHOLDERS } from './adConfig';

// Re-exported for backwards compatibility with existing imports.
export { ADSENSE_CLIENT };

/**
 * A single Google AdSense ad unit.
 *
 * Renders an <ins class="adsbygoogle"> element and asks AdSense to fill it.
 * The AdSense loader script itself is included once in public/index.html.
 *
 * In development (or when REACT_APP_SHOW_AD_PLACEHOLDERS=true) a labeled grey placeholder box is
 * rendered instead, so you can see exactly where and what size the ads will be without a live
 * AdSense account.
 *
 * Props:
 *  - slot:       (string) the ad unit slot id from your AdSense dashboard. Required to serve real ads.
 *  - format:     (string) data-ad-format, defaults to 'auto'.
 *  - responsive: (bool)   data-full-width-responsive, defaults to true.
 *  - style:      (object) inline style for the <ins> element.
 *  - label:      (string) optional text shown on the dev placeholder (e.g. "Side Rail 160x600").
 */
export default function GoogleAd({ slot, format = 'auto', responsive = true, style, label }) {
    const insRef = useRef(null);

    useEffect(() => {
        if (SHOW_AD_PLACEHOLDERS) {
            return; // placeholder mode - don't call AdSense
        }
        try {
            // Only push once per mounted <ins>. AdSense throws if we push an already-filled slot.
            if (insRef.current && !insRef.current.getAttribute('data-adsbygoogle-status')) {
                (window.adsbygoogle = window.adsbygoogle || []).push({});
            }
        } catch (e) {
            // Swallow: ad blockers / not-yet-approved account will throw; the page must keep working.
            console.warn('AdSense push skipped:', e?.message);
        }
    }, []);

    if (SHOW_AD_PLACEHOLDERS) {
        const baseStyle = style || { display: 'block' };
        const placeholderStyle = {
            ...baseStyle,
            minHeight: baseStyle.height || 90,
            background: 'repeating-linear-gradient(45deg, #eaeaea, #eaeaea 10px, #f4f4f4 10px, #f4f4f4 20px)',
            border: '1px dashed #b5b5b5',
            color: '#777',
            fontSize: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            boxSizing: 'border-box',
        };
        return (
            <div style={placeholderStyle}>
                <span>Ad placeholder{label ? ` \u2014 ${label}` : ''}<br />(slot {slot})</span>
            </div>
        );
    }

    return (
        <ins
            ref={insRef}
            className="adsbygoogle"
            style={style || { display: 'block' }}
            data-ad-client={ADSENSE_CLIENT}
            data-ad-slot={slot}
            data-ad-format={format}
            data-full-width-responsive={responsive ? 'true' : 'false'}
        />
    );
}
